<template>
  <div class="p-4">
    <template v-if="groupedResults == null">
      <label>No records to display</label>
    </template>
    <div
      v-for="result in groupedResults"
      :key="result.id"
      class="form-result bg-white"
    >
      <h4 class="form-name">{{ result.form.name }}</h4>
      <span v-if="result.form.subtitle" class="form-subtitle">
        {{ result.form.subtitle }}
      </span>
      <span class="step" />
      <chart
        v-if="result.allNumbers"
        :data="result.lineChart.data"
        :options="result.lineChart.options"
        :plugins="plugins"
        type="line"
        class="line-chart"
      />
      <div v-else class="pt-4 pb-2">
        <b-table
          class="omt-results-table"
          striped
          hover
          :items="result.scores"
          bordered
          borderless
          small
        >
          <template #cell(date)="data">
            {{ formatDateOnly(data.item.date) }}
          </template>
          <template #table-colgroup="scope">
            <col
              v-for="field in scope.fields"
              :key="field.key"
              :style="{ width: field.key === 'date' ? '120px' : 'initial' }"
            />
          </template>
        </b-table>
      </div>
      <div
        v-if="result.form.documentInstructions"
        v-html-sanitize="result.form.documentInstructions"
        class="form-instructions"
      />
    </div>
  </div>
</template>

<script>
import ChartDataLabels from "chartjs-plugin-datalabels";
import documentServer from "../../server/document";
import { groupBy } from "../../helpers/general";
import { formatDateOnlyPrettyShort } from "../../helpers/date";
import Chart from "../controls/Chart.vue";
import { mapGetters } from "vuex";

export default {
  name: "EocViewOmt",
  components: { Chart },
  props: ["clientId", "eocId", "showing"],
  data() {
    return {
      results: [],
      plugins: [ChartDataLabels]
    };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.results.splice(0);
      const results = await documentServer.getOmtResults(this.eocId);
      this.results = results.sort((a, b) =>
        a.effectiveDate.localeCompare(b.effectiveDate)
      );
    }
  },
  computed: {
    ...mapGetters("layout", ["hasCurrentUserDocumentPermission"]),
    groupedResults() {
      return this.results.length > 0
        ? Array.from(groupBy(this.results, d => d.formId).values()).map(g => {
            const allNumbers = g.filter(d => isNaN(d.score)).length == 0;
            const chartData = g.map(d => d.score ?? 0);
            const scores = g.map(d => ({
              date: d.effectiveDate,
              result: d.score
            }));
            return {
              form: g[0].form,
              allNumbers,
              scores,
              lineChart: {
                data: {
                  labels: g.map(d =>
                    formatDateOnlyPrettyShort(d.effectiveDate)
                  ),
                  datasets: [
                    {
                      label: "Score",
                      borderColor: "#2196F3",
                      pointBackgroundColor: "white",
                      pointHoverBackgroundColor: "white",
                      pointBorderColor: "rgba(21, 150, 243, 1)",
                      backgroundColor: "transparent",
                      data: chartData,
                      cubicInterpolationMode: "monotone"
                    }
                  ]
                },
                options: {
                  maintainAspectRatio: false,
                  radius: 0,
                  elements: {
                    point: {
                      radius: 9,
                      hoverRadius: 9,
                      borderWidth: 4,
                      hoverBorderWidth: 4
                    }
                  },
                  interaction: { intersect: false },
                  scales: {
                    y: {
                      grace: 1,
                      ticks: { min: 0, display: false },
                      grid: {
                        drawOnChartArea: true,
                        drawBorder: false,
                        display: true
                      }
                    },
                    x: {
                      offset: true,
                      ticks: {
                        fontFamily: "Roboto",
                        fontSize: "14",
                        fontColor: "#BABABA"
                      },
                      grid: {
                        drawOnChartArea: false,
                        drawBorder: true,
                        display: true
                      }
                    }
                  },
                  plugins: {
                    datalabels: { anchor: "end", align: "end" },
                    legend: { display: false },
                    tooltip: {
                      padding: 8,
                      enabled: false,
                      external: context => {
                        const { tooltip } = context;
                        const tooltipEl = getOrCreateTooltip(context);

                        if (tooltip.opacity === 0) {
                          tooltipEl.style.opacity = 0;
                          return;
                        }

                        if (tooltip.body) {
                          const index = tooltip.dataPoints[0].dataIndex;
                          const result = g[index];
                          const href = `/#/document/${result.id}`;
                          appendTooltipBody(
                            context,
                            href,
                            this.hasCurrentUserDocumentPermission(
                              result.formId,
                              "isReadOnly"
                            )
                          );
                        }

                        positionTooltip(context);
                      }
                    }
                  }
                }
              }
            };
          })
        : null;
    }
  },
  watch: {
    showing(newValue) {
      if (newValue) {
        this.fetchData();
      }
    }
  }
};

const getOrCreateTooltip = context => {
  const { chart } = context;
  let tooltipEl = chart.canvas.parentNode.querySelector("div");
  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.classList.add("tooltip");

    const table = document.createElement("table");
    table.style.margin = "0px";

    tooltipEl.appendChild(table);

    tooltipEl.addEventListener("mouseout", () => {
      tooltipEl.style.opacity = 0;
    });

    tooltipEl.addEventListener("mouseover", () => {
      tooltipEl.style.opacity = 1;
    });

    chart.canvas.parentNode.appendChild(tooltipEl);
  }

  return tooltipEl;
};

const appendTooltipBody = (context, href, hasReadAccess) => {
  const { tooltip } = context;
  const tooltipEl = getOrCreateTooltip(context);
  const tableBody = document.createElement("tbody");
  const bodyLines = tooltip.body.map(b => b.lines);

  bodyLines.forEach(body => {
    const tr = document.createElement("tr");
    tr.style.backgroundColor = "inherit";
    tr.style.borderWidth = 0;

    const td = document.createElement("td");
    td.style.borderWidth = 0;

    const text = document.createTextNode(body);

    td.appendChild(text);
    tr.appendChild(td);
    tableBody.appendChild(tr);
  });

  if (hasReadAccess) {
    const tr = document.createElement("tr");
    const td = document.createElement("td");
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.innerText = "Open Result";

    td.appendChild(anchor);
    tr.appendChild(td);
    tableBody.appendChild(tr);
  }

  const tableRoot = tooltipEl.querySelector("table");
  while (tableRoot.firstChild) {
    tableRoot.firstChild.remove();
  }

  tableRoot.appendChild(tableBody);
};

const positionTooltip = context => {
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(context);
  const position = chart.canvas.getBoundingClientRect();

  tooltipEl.style.opacity = 1;
  tooltipEl.style.left =
    position.left + window.pageXOffset + tooltip.caretX + "px";
  tooltipEl.style.top =
    position.top +
    window.pageYOffset +
    tooltip.caretY -
    tooltip.height -
    20 +
    "px";
  tooltipEl.style.padding =
    tooltip.options.padding + "px " + tooltip.options.padding + "px";
};
</script>

<style scoped>
.form-result {
  padding: 30px 40px 20px;
  border-radius: 12px;
}

.form-result:not(:last-of-type) {
  margin-bottom: 32px;
}

.form-name {
  font-size: 1.1rem;
  margin-bottom: 0px;
}

.form-subtitle {
  display: block;
  color: #aaa;
  font-size: 15px;
  margin-top: 6px;
}

.form-instructions {
  color: #888;
  font-size: 15px;
  margin-top: 12px;
}

.form-instructions p:last-of-type {
  margin-bottom: 0;
}

.step {
  display: block;
  width: 42px;
  height: 5px;
  border-radius: 5px;
  background-image: linear-gradient(to right, #0783fb, #7168f0);
  margin-top: 11px;
}

.line-chart {
  height: 250px;
  width: 100%;
  margin: 12px -5px 0;
}

.line-chart >>> .tooltip {
  background: rgba(0, 0, 0, 0.75);
  border-radius: 6px;
  position: "absolute";
  transform: translate(-50%, 0);
  transition: all 0.1s ease;
}

.line-chart >>> .tooltip::after {
  content: "";
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  border-left: solid 5px transparent;
  border-right: solid 5px transparent;
  border-top: solid 5px rgba(0, 0, 0, 0.75);
}

.line-chart >>> .tooltip table tr,
.line-chart >>> .tooltip table th,
.line-chart >>> .tooltip table td {
  color: white;
  border-width: 0;
}

.line-chart >>> .tooltip a {
  color: white;
  text-decoration: underline;
  /* pointer-events: all; */
}

.omt-results-table >>> tbody tr:nth-of-type(odd) {
  background-color: #eff4fb;
}
.omt-results-table >>> th,
.omt-results-table >>> td {
  font-size: 14px;
}
</style>
